'use client'

import { Badge, EmptyState, Skeleton } from '@repo/ui'
import { cn } from '@repo/ui'
import { BookOpen, CheckCircle2 } from 'lucide-react'

import type { KnowledgeBase } from '@/types/knowledge'

interface KnowledgeTabProps {
  knowledgeBases: KnowledgeBase[]
  linkedKbIds: string[]
  isLoading: boolean
  onChange: (kbIds: string[]) => void
}

export function KnowledgeTab({
  knowledgeBases,
  linkedKbIds,
  isLoading,
  onChange,
}: KnowledgeTabProps) {
  const toggle = (kbId: string) => {
    onChange(
      linkedKbIds.includes(kbId)
        ? linkedKbIds.filter((id) => id !== kbId)
        : [...linkedKbIds, kbId]
    )
  }
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-foreground text-sm font-semibold">
          Bases vinculadas
        </h3>
        <p className="text-muted-foreground mt-0.5 text-xs">
          Vincule uma ou mais bases de conhecimento para enriquecer as respostas
          do agente.
        </p>
      </div>

      {isLoading ? (
        <div className="grid gap-3 md:grid-cols-2">
          <Skeleton className="h-20 rounded-lg" />
          <Skeleton className="h-20 rounded-lg" />
        </div>
      ) : knowledgeBases.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title="Nenhuma base de conhecimento"
          description="Crie uma base em Conhecimento para vincular ao agente."
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {knowledgeBases.map((kb) => {
            const isLinked = linkedKbIds.includes(kb.id)
            return (
              <button
                key={kb.id}
                type="button"
                onClick={() => toggle(kb.id)}
                className={cn(
                  'relative flex flex-col items-start gap-1 rounded-lg border p-4 text-left transition-colors',
                  isLinked
                    ? 'border-primary/40 bg-primary/5'
                    : 'border-border bg-card hover:bg-muted/40'
                )}
              >
                {isLinked && (
                  <CheckCircle2 className="text-primary absolute top-3 right-3 size-4" />
                )}
                <span className="text-foreground pr-6 text-sm font-medium">
                  {kb.name}
                </span>
                <span className="text-muted-foreground text-xs">
                  {kb.description ?? 'Sem descrição'}
                </span>
                <Badge variant="secondary" className="mt-1 text-xs">
                  {kb._count?.articles ?? 0} artigos
                </Badge>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
